import React, { useState, useEffect } from 'react';
import { Settings, MessageCircle, FileSpreadsheet, X, Moon, Sun, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import useI18n from '../hooks/useI18n';

const AppBar = ({ filename, sheetCount, onSettingsOpen, onChatToggle, chatOpen, hasData }) => {
  const { t, lang, switchLang } = useI18n();
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [dark]);

  return (
    <TooltipProvider>
      <header className="app-bar" role="banner">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          {/* Left side */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <FileSpreadsheet className="w-4 h-4 text-primary-foreground" />
              </div>
              <h1 className="text-lg font-semibold tracking-tight hidden sm:block">{t('app.title')}</h1>
            </div>
            {hasData && filename && (
              <Badge
                variant="secondary"
                className="font-mono text-xs"
                data-testid="dataset-status-chip"
              >
                {filename} · {sheetCount} sheet{sheetCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {hasData && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={chatOpen ? 'default' : 'outline'}
                    size="sm"
                    onClick={onChatToggle}
                    data-testid="chat-toggle-button"
                    className="gap-1.5"
                  >
                    {chatOpen ? <X className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                    <span className="hidden sm:inline">{chatOpen ? t('app.closeChat') : t('app.askAi')}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{chatOpen ? t('app.closeChat') : t('app.askAi')}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => switchLang(lang === 'en' ? 'fr' : 'en')}
                  data-testid="lang-toggle-button"
                  className="gap-1.5"
                  aria-label="Toggle language"
                >
                  <Languages className="w-4 h-4" />
                  <span className="text-xs uppercase font-medium">{lang}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{lang === 'en' ? 'Passer en francais' : 'Switch to English'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDark(!dark)}
                  data-testid="theme-toggle-button"
                >
                  {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{dark ? t('app.lightMode') : t('app.darkMode')}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onSettingsOpen}
                  data-testid="settings-open-button"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('app.settings')}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
};

export default AppBar;
